import { Dispatch, useCallback, useReducer, useRef } from 'react';

import { PresenceTypeEnum } from '@/types/presences';

import { DEFAULT_ITEM } from '../constants';
import { ItemLayoutType, TimeKeepingRecords, TimeKeepingSupportRegistry } from '../types';
import { buildCompositeKey } from '../utils/composite-key';
import { findFreeSpot } from '../utils/find-free-spot';
import { reflowItems } from '../utils/reflow-items';

// ─── Reducer ────────────────────────────────────────────────────────────────

export type LayoutAction =
  | {
      type: 'ADD_ITEM';
      presenceType: PresenceTypeEnum;
      presenceId: string;
      sessionSupports: string[];
      cols: number;
    }
  | { type: 'REMOVE_ITEM'; compositeId: string }
  | { type: 'REFLOW'; cols: number }
  | { type: 'RESTORE'; items: ItemLayoutType[] }
  | { type: 'RESET' }
  | { type: 'UPDATE_SESSION_SUPPORTS'; added: string[]; removed: string[] }
  | { type: 'SET_ITEM_SUPPORTS'; compositeId: string; selectedIds: string[] }
  | {
      type: 'ADD_RECORD';
      compositeId: string;
      supportPresenceId: string;
      record: TimeKeepingRecords;
    }
  | {
      type: 'UPDATE_RECORD';
      compositeId: string;
      supportPresenceId: string;
      recordIndex: number;
      partial: Partial<TimeKeepingRecords>;
    }
  | { type: 'REMOVE_RECORD'; compositeId: string; supportPresenceId: string; recordIndex: number }
  | { type: 'RESET_RECORDS'; compositeId: string; supportPresenceId?: string };

const toRegistries = (ids: string[]): TimeKeepingSupportRegistry[] =>
  ids.map(id => ({ presenceId: id, records: [] }));

export function layoutReducer(state: ItemLayoutType[], action: LayoutAction): ItemLayoutType[] {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { presenceType, presenceId, sessionSupports, cols } = action;
      if (state.some(it => it.presence_type === presenceType && it.presence_id === presenceId)) {
        return state;
      }
      const { x, y } = findFreeSpot(state, DEFAULT_ITEM.w, DEFAULT_ITEM.h, cols);
      const base: ItemLayoutType = {
        presence_type: presenceType,
        presence_id: presenceId,
        x,
        y,
        supports: [],
        ...DEFAULT_ITEM,
      };
      if (presenceType === PresenceTypeEnum.PEOPLE) {
        return [...state, { ...base, records: [] }];
      }
      return [...state, { ...base, supports: toRegistries(sessionSupports) }];
    }

    case 'REMOVE_ITEM': {
      const exists = state.some(
        it => buildCompositeKey(it.presence_type, it.presence_id) === action.compositeId
      );
      if (!exists) return state;
      return state.filter(
        it => buildCompositeKey(it.presence_type, it.presence_id) !== action.compositeId
      );
    }

    case 'REFLOW': {
      const overflows = state.some(it => it.x + it.w > action.cols);
      return overflows ? reflowItems(state, action.cols) : state;
    }

    case 'RESTORE':
      return action.items;

    case 'RESET':
      return [];

    case 'UPDATE_SESSION_SUPPORTS': {
      const { added, removed } = action;
      if (added.length === 0 && removed.length === 0) return state;
      return state.map(item => {
        if (item.presence_type === PresenceTypeEnum.PEOPLE) return item;
        let next = item.supports;
        if (added.length > 0) {
          next = [
            ...next,
            ...added
              .filter(id => !next.some(s => s.presenceId === id))
              .map(id => ({ presenceId: id, records: [] })),
          ];
        }
        if (removed.length > 0) {
          next = next.filter(s => !removed.includes(s.presenceId));
        }
        return next === item.supports ? item : { ...item, supports: next };
      });
    }

    case 'SET_ITEM_SUPPORTS':
      return state.map(item => {
        if (buildCompositeKey(item.presence_type, item.presence_id) !== action.compositeId) {
          return item;
        }
        return {
          ...item,
          supports: action.selectedIds.map(
            id => item.supports.find(s => s.presenceId === id) ?? { presenceId: id, records: [] }
          ),
        };
      });

    case 'ADD_RECORD':
      return state.map(item => {
        if (buildCompositeKey(item.presence_type, item.presence_id) !== action.compositeId) {
          return item;
        }
        if (item.presence_type === PresenceTypeEnum.PEOPLE) {
          return { ...item, records: [...(item.records ?? []), action.record] };
        }
        return {
          ...item,
          supports: item.supports.map(s =>
            s.presenceId !== action.supportPresenceId
              ? s
              : { ...s, records: [...s.records, action.record] }
          ),
        };
      });

    case 'UPDATE_RECORD':
      return state.map(item => {
        if (buildCompositeKey(item.presence_type, item.presence_id) !== action.compositeId) {
          return item;
        }
        if (item.presence_type === PresenceTypeEnum.PEOPLE) {
          return {
            ...item,
            records: (item.records ?? []).map((r, i) =>
              i !== action.recordIndex ? r : { ...r, ...action.partial }
            ),
          };
        }
        return {
          ...item,
          supports: item.supports.map(s =>
            s.presenceId !== action.supportPresenceId
              ? s
              : {
                  ...s,
                  records: s.records.map((r, i) =>
                    i !== action.recordIndex ? r : { ...r, ...action.partial }
                  ),
                }
          ),
        };
      });

    case 'REMOVE_RECORD':
      return state.map(item => {
        if (buildCompositeKey(item.presence_type, item.presence_id) !== action.compositeId) {
          return item;
        }
        if (item.presence_type === PresenceTypeEnum.PEOPLE) {
          return {
            ...item,
            records: (item.records ?? []).filter((_, i) => i !== action.recordIndex),
          };
        }
        return {
          ...item,
          supports: item.supports.map(s =>
            s.presenceId !== action.supportPresenceId
              ? s
              : { ...s, records: s.records.filter((_, i) => i !== action.recordIndex) }
          ),
        };
      });

    case 'RESET_RECORDS':
      return state.map(item => {
        if (buildCompositeKey(item.presence_type, item.presence_id) !== action.compositeId) {
          return item;
        }
        if (item.presence_type === PresenceTypeEnum.PEOPLE) {
          return { ...item, records: [] };
        }
        return {
          ...item,
          supports: item.supports.map(s =>
            action.supportPresenceId === undefined || s.presenceId === action.supportPresenceId
              ? { ...s, records: [] }
              : s
          ),
        };
      });

    default:
      return state;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export type UseTimeKeepingLayoutReturn = {
  items: ItemLayoutType[];
  dispatch: Dispatch<LayoutAction>;
  colsRef: React.MutableRefObject<number>;
  handleColsChange: (cols: number) => void;
};

export function useTimeKeepingLayout(): UseTimeKeepingLayoutReturn {
  const [items, dispatch] = useReducer(layoutReducer, []);
  const colsRef = useRef(1);

  const handleColsChange = useCallback((cols: number) => {
    if (colsRef.current === cols) return;
    colsRef.current = cols;
    dispatch({ type: 'REFLOW', cols });
  }, []);

  return { items, dispatch, colsRef, handleColsChange };
}
